import type {
  CallScriptHistoryPage,
  CallScriptInterface,
  CallScriptLibraryStep,
  CallScriptPromptEntry,
  CallScriptSequenceUse,
  CallScriptUpsertBody,
  CallScriptVersionInterface,
  ResolvedCallScript
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

export interface CallScriptsPromptResponse {
  text: string;
  entries: CallScriptPromptEntry[];
  /** Slugs left out because the section budget was reached. */
  dropped: string[];
  /** A script in force contains "Take them to the rental page" and the rental URL resolves here (ENG-3363). */
  hasRentalPageStep: boolean;
  /** The sequence day's script, rendered first, when the conversation came from a bound day (ENG-3403). */
  startingScriptSlug?: string;
}

/** A script in force with the org's resolved library attached (ENG-3402) — what the coaching engine judges. */
export interface CallScriptResolvedResponse {
  scripts: Array<ResolvedCallScript & { library: CallScriptLibraryStep[] }>;
}

// LINK apps/server/src/API/CallScript/CallScript.Controller.ts
export class CallScriptAPI {
  /** Every call-script row in the org, all scopes — Studio resolves inheritance client-side with `resolveCallScripts`. */
  static async listRows(orgId: number): Promise<CallScriptInterface[]> {
    return APIService.Server.get<CallScriptInterface[]>(`/call-scripts/org/${orgId}`);
  }

  /** Create or update the script at the body's scope. Free text is LLM-checked on the server; a rejection is a 4xx with the notes. */
  static async upsert(orgId: number, body: CallScriptUpsertBody): Promise<CallScriptInterface> {
    return APIService.Server.put<CallScriptInterface>(`/call-scripts/org/${orgId}`, body);
  }

  /** Revert to inherited — soft-deletes the override row (a version row records it). */
  static async remove(orgId: number, id: number): Promise<{ ok: true }> {
    return APIService.Server.delete<{ ok: true }>(`/call-scripts/org/${orgId}/${id}`);
  }

  static async listVersions(orgId: number, id: number): Promise<CallScriptVersionInterface[]> {
    return APIService.Server.get<CallScriptVersionInterface[]>(`/call-scripts/org/${orgId}/${id}/versions`);
  }

  /** Save the given version's snapshot as a NEW revision. */
  static async restore(orgId: number, id: number, revision: number): Promise<CallScriptInterface> {
    return APIService.Server.post<CallScriptInterface>(`/call-scripts/org/${orgId}/${id}/restore/${revision}`, {});
  }

  /** Org-wide history of script and step changes, newest first, deleted subjects included. `before` = the previous page's `nextBefore`. */
  static async listHistory(orgId: number, before?: string): Promise<CallScriptHistoryPage> {
    return APIService.Server.get<CallScriptHistoryPage>(`/call-scripts/org/${orgId}/history`, before ? { before } : undefined);
  }

  /** Every sequence-day binding in the org with the script slug it names (ENG-3403). */
  static async listSequenceUses(orgId: number): Promise<Array<CallScriptSequenceUse & { slug: string }>> {
    return APIService.Server.get<Array<CallScriptSequenceUse & { slug: string }>>(`/call-scripts/org/${orgId}/sequence-uses`);
  }

  /** Bot-facing (INTERNAL): the scripts in force for a conversation with each step's library entry — for the coaching step lane (ENG-3402). */
  static async getResolved(orgId: number, locationId?: number, sequenceStepKey?: string): Promise<CallScriptResolvedResponse> {
    return APIService.Server.get<CallScriptResolvedResponse>(`/call-scripts/org/${orgId}/resolved`, { locationId, sequenceStepKey });
  }

  /** Bot-facing (INTERNAL): the rendered prompt section for this scope; `sequenceStepKey` = the ladder day that opened the conversation (ENG-3403). */
  static async getPromptText(orgId: number, locationId?: number, sequenceStepKey?: string): Promise<CallScriptsPromptResponse> {
    return APIService.Server.get<CallScriptsPromptResponse>(`/call-scripts/org/${orgId}/prompt`, { locationId, sequenceStepKey });
  }
}
